import type {
  BookingExclusionMatch,
  CampaignDiscountBookingExclusionInput,
  CampaignDiscountBookingExclusionRecord
} from '@navio/nsdk';
import { z } from 'zod';

export type { BookingExclusionMatch };

export const BOOKING_EXCLUSION_MATCH = {
  any: 'any',
  products: 'products',
  tags: 'tags'
} as const satisfies Record<string, BookingExclusionMatch>;

export type BookingExclusionRow = {
  brandId: number | null;
  match: BookingExclusionMatch;
  salesItemIds: number[];
  salesItemTagIds: number[];
};

export const EXCLUDE_IF_BOOKING_EXISTS_LABEL = 'Exclude if booking exists';
export const EXCLUDE_IF_BOOKING_EXISTS_TOOLTIP =
  'Skip issuing this discount when the customer group already has a booking that matches a row. An empty list issues the discount to everyone who joins.';

export const bookingExclusionRowSchema = z.object({
  brandId: z.number().nullable(),
  match: z.enum([BOOKING_EXCLUSION_MATCH.any, BOOKING_EXCLUSION_MATCH.products, BOOKING_EXCLUSION_MATCH.tags]),
  salesItemIds: z.array(z.number()),
  salesItemTagIds: z.array(z.number())
});

export const emptyBookingExclusion = (brandId: number): BookingExclusionRow => ({
  brandId,
  match: BOOKING_EXCLUSION_MATCH.any,
  salesItemIds: [],
  salesItemTagIds: []
});

export const bookingExclusionRowsFromSources = (
  sources: CampaignDiscountBookingExclusionRecord[]
): BookingExclusionRow[] =>
  sources.map((source) => ({
    brandId: source.brand.id,
    match: source.match,
    salesItemIds: source.salesItems.map((item) => item.id),
    salesItemTagIds: source.salesItemTags.map((tag) => tag.id)
  }));

export const bookingExclusionsForSubmit = (rows: BookingExclusionRow[]): CampaignDiscountBookingExclusionInput[] =>
  rows.flatMap((row) => {
    if (row.brandId == null) {
      return [];
    }
    return [
      {
        brandId: row.brandId,
        match: row.match,
        salesItemIds: row.match === BOOKING_EXCLUSION_MATCH.products ? row.salesItemIds : [],
        salesItemTagIds: row.match === BOOKING_EXCLUSION_MATCH.tags ? row.salesItemTagIds : []
      }
    ];
  });

export const formatBookingExclusionLine = (source: CampaignDiscountBookingExclusionRecord): string => {
  if (source.match === BOOKING_EXCLUSION_MATCH.any) {
    return `${source.brand.name}, any booking`;
  }
  if (source.match === BOOKING_EXCLUSION_MATCH.products) {
    const names = source.salesItems.map((item) => item.name).join(', ');
    return `${source.brand.name}, ${names || 'specific products'}`;
  }
  const names = source.salesItemTags.map((tag) => tag.name).join(', ');
  return `${source.brand.name}, ${names || 'specific product tags'}`;
};

export const refineBookingExclusions = (
  value: { bookingExclusions: BookingExclusionRow[] },
  ctx: z.RefinementCtx
): void => {
  const seen = new Set<number>();
  value.bookingExclusions.forEach((row, index) => {
    if (row.brandId == null) {
      ctx.addIssue({ code: 'custom', message: 'Pick a brand', path: ['bookingExclusions', index, 'brandId'] });
      return;
    }
    if (seen.has(row.brandId)) {
      ctx.addIssue({
        code: 'custom',
        message: 'Each brand can be listed once',
        path: ['bookingExclusions', index, 'brandId']
      });
    }
    seen.add(row.brandId);
    if (row.match === BOOKING_EXCLUSION_MATCH.products && row.salesItemIds.length === 0) {
      ctx.addIssue({
        code: 'custom',
        message: 'Pick at least one product',
        path: ['bookingExclusions', index, 'salesItemIds']
      });
    }
    if (row.match === BOOKING_EXCLUSION_MATCH.tags && row.salesItemTagIds.length === 0) {
      ctx.addIssue({
        code: 'custom',
        message: 'Pick at least one product tag',
        path: ['bookingExclusions', index, 'salesItemTagIds']
      });
    }
  });
};
